import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';

const ALL_EVENTS = [
  { value: 'order.status_changed',    label: 'Order Status Changed' },
  { value: 'inquiry.created',         label: 'Inquiry Created' },
  { value: 'inquiry.status_changed',  label: 'Inquiry Status Changed' },
  { value: 'client.created',          label: 'Client Created' },
  { value: 'client.updated',          label: 'Client Updated' },
  { value: 'payment.received',        label: 'Payment Received' },
  { value: '*',                        label: 'All Events (wildcard)' },
];

const defaultForm = { name: '', url: '', events: [], secret: '', is_active: true };

function StatusBadge({ code }) {
  if (code === null || code === undefined) return <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Never fired</span>;
  const ok = code >= 200 && code < 300;
  return (
    <span style={{
      fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: 10,
      background: ok ? '#dcfce7' : '#fee2e2',
      color: ok ? '#15803d' : '#dc2626',
    }}>
      {code === 0 ? 'Error' : code}
    </span>
  );
}

function EventPill({ event }) {
  return (
    <span style={{
      fontSize: '0.68rem', fontWeight: 600, padding: '2px 7px', borderRadius: 10,
      background: 'var(--primary-lighter)', color: 'var(--primary)',
      whiteSpace: 'nowrap',
    }}>
      {event === '*' ? 'all events' : event}
    </span>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: type === 'error' ? 'var(--error)' : '#16a34a',
      color: '#fff', padding: '12px 20px', borderRadius: 10,
      boxShadow: 'var(--shadow-md)', fontWeight: 600, fontSize: '0.875rem',
      display: 'flex', alignItems: 'center', gap: 10, minWidth: 240,
    }}>
      <span>{type === 'error' ? '✗' : '✓'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>×</button>
    </div>
  );
}

export default function WebhookSettings() {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const fetchHooks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/webhook-subscriptions');
      setHooks(data.data || []);
    } catch {
      showToast('Failed to load webhook subscriptions', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchHooks(); }, [fetchHooks]);

  const openAdd = () => {
    setEditTarget(null);
    setForm(defaultForm);
    setModalOpen(true);
  };

  const openEdit = (hook) => {
    setEditTarget(hook);
    const events = Array.isArray(hook.events) ? hook.events : JSON.parse(hook.events || '[]');
    setForm({
      name: hook.name,
      url: hook.url,
      events,
      secret: hook.secret || '',
      is_active: hook.is_active === 1 || hook.is_active === true,
    });
    setModalOpen(true);
  };

  const toggleEvent = (val) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(val) ? f.events.filter(e => e !== val) : [...f.events, val],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim() || form.events.length === 0) {
      showToast('Name, URL and at least one event are required', 'error');
      return;
    }
    try { new URL(form.url); } catch {
      showToast('Please enter a valid URL', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        url: form.url.trim(),
        events: form.events,
        secret: form.secret.trim() || null,
        is_active: form.is_active ? 1 : 0,
      };
      if (editTarget) {
        await api.put(`/webhook-subscriptions/${editTarget.id}`, payload);
        showToast('Webhook updated successfully');
      } else {
        await api.post('/webhook-subscriptions', payload);
        showToast('Webhook created successfully');
      }
      setModalOpen(false);
      fetchHooks();
    } catch (err) {
      showToast(err?.response?.data?.error || 'Failed to save webhook', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (hook) => {
    try {
      await api.put(`/webhook-subscriptions/${hook.id}`, { is_active: hook.is_active ? 0 : 1 });
      fetchHooks();
    } catch {
      showToast('Failed to update webhook status', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/webhook-subscriptions/${id}`);
      setDeleteConfirm(null);
      showToast('Webhook deleted');
      fetchHooks();
    } catch {
      showToast('Failed to delete webhook', 'error');
    }
  };

  const handleTest = async (hook) => {
    setTestingId(hook.id);
    try {
      await api.post(`/webhook-subscriptions/${hook.id}/test`);
      showToast(`Test delivery fired to ${hook.url}`);
      setTimeout(fetchHooks, 1500);
    } catch (err) {
      showToast(err?.response?.data?.error || 'Test delivery failed', 'error');
    } finally {
      setTestingId(null);
    }
  };

  const parseEvents = (raw) => {
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw || '[]'); } catch { return []; }
  };

  return (
    <div style={{ padding: '24px', maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem', color: 'var(--text-primary)' }}>Webhook Integrations</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Notify external systems when events occur in the CRM. Deliveries are signed with HMAC-SHA256 when a secret is set.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Webhook
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner spinner-lg" /></div>
      ) : hooks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 64, color: 'var(--text-muted)' }}>
          <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.3 }}>
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
          </svg>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No webhooks configured</div>
          <div style={{ fontSize: '0.82rem' }}>Add a webhook to start sending CRM events to external systems.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hooks.map(hook => {
            const events = parseEvents(hook.events);
            const isActive = hook.is_active === 1 || hook.is_active === true;
            return (
              <div key={hook.id} className="card" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  {/* Status indicator */}
                  <div style={{ paddingTop: 2 }}>
                    <button
                      onClick={() => handleToggleActive(hook)}
                      title={isActive ? 'Click to disable' : 'Click to enable'}
                      style={{
                        width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: isActive ? 'var(--primary)' : '#d1d5db',
                        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                        padding: 0,
                      }}
                    >
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3, transition: 'left 0.2s',
                        left: isActive ? 19 : 3,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  </div>

                  {/* Main content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{hook.name}</span>
                      {!isActive && (
                        <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '1px 7px', borderRadius: 8, background: '#f3f4f6', color: '#6b7280' }}>Disabled</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 8, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hook.url}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                      {events.map(ev => <EventPill key={ev} event={ev} />)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      <span>Last status: <StatusBadge code={hook.last_status} /></span>
                      {hook.failure_count > 0 && (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>{hook.failure_count} failure{hook.failure_count !== 1 ? 's' : ''}</span>
                      )}
                      {hook.last_triggered_at && (
                        <span>Last fired: {new Date(hook.last_triggered_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn"
                      onClick={() => handleTest(hook)}
                      disabled={testingId === hook.id}
                      style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                      title="Send a test delivery"
                    >
                      {testingId === hook.id ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> : 'Test'}
                    </button>
                    <button
                      className="btn"
                      onClick={() => openEdit(hook)}
                      style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn"
                      onClick={() => setDeleteConfirm(hook)}
                      style={{ fontSize: '0.78rem', padding: '5px 12px', color: 'var(--error)', borderColor: 'var(--error)' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Webhook' : 'Add Webhook'}>
          <form onSubmit={handleSave}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 5, color: 'var(--text-secondary)' }}>
                  Name <span style={{ color: 'var(--error)' }}>*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="e.g. Slack Order Alerts"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 5, color: 'var(--text-secondary)' }}>
                  Endpoint URL <span style={{ color: 'var(--error)' }}>*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="https://example.com/webhook"
                  value={form.url}
                  onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                  required
                  type="url"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                  Events to subscribe <span style={{ color: 'var(--error)' }}>*</span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {ALL_EVENTS.map(ev => (
                    <label key={ev.value} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={form.events.includes(ev.value)}
                        onChange={() => toggleEvent(ev.value)}
                        style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>{ev.label}</span>
                      <code style={{ fontSize: '0.7rem', color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 5 }}>{ev.value}</code>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: 5, color: 'var(--text-secondary)' }}>
                  Secret <span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-muted)' }}>(optional — used for HMAC-SHA256 signature)</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Leave blank to skip signing"
                  value={form.secret}
                  onChange={e => setForm(f => ({ ...f, secret: e.target.value }))}
                  type="password"
                  autoComplete="new-password"
                />
                {form.secret && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Signature sent in <code style={{ background: 'var(--surface-2)', padding: '0 4px', borderRadius: 3 }}>X-Webhook-Signature</code> header as <code style={{ background: 'var(--surface-2)', padding: '0 4px', borderRadius: 3 }}>sha256=...</code>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>Active</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>(uncheck to pause deliveries)</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" className="btn" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Saving…</> : (editTarget ? 'Save Changes' : 'Create Webhook')}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Webhook">
          <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
